'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Plan } from '@/core/domain';
import type { GetAllPlansUseCase } from '../../domain/usecases/get-all-plans';
import type { GetPlanByIdUseCase } from '../../domain/usecases/get-plan-by-id';
import type { CreatePlanUseCase } from '../../domain/usecases/create-plan';
import type { UpdatePlanUseCase } from '../../domain/usecases/update-plan';
import type { DeletePlanUseCase } from '../../domain/usecases/delete-plan';

export interface PlansState {
  /** Lista vinda do refresh (getAll). */
  plans: Plan[];
  /** Plano em foco após getById; independente da lista. */
  currentPlan: Plan | null;
  loading: boolean;
  error: string | null;
}

export interface PlanOperationResult {
  success: boolean;
  error: string | null;
}

export interface PlansEvents {
  /** Carrega todos os planos e atualiza `state.plans`. */
  refresh: () => Promise<void>;
  getById: (id: string) => Promise<Plan | null>;
  clearCurrentPlan: () => void;
  create: (plan: Plan) => Promise<PlanOperationResult>;
  update: (id: string, data: Partial<Plan>) => Promise<PlanOperationResult>;
  delete: (id: string) => Promise<PlanOperationResult>;
  clearError: () => void;
}

export interface PlansContextValue {
  state: PlansState;
  events: PlansEvents;
}

export interface PlansProviderProps {
  children: React.ReactNode;
  getAllPlansUseCase: GetAllPlansUseCase;
  getPlanByIdUseCase: GetPlanByIdUseCase;
  createPlanUseCase: CreatePlanUseCase;
  updatePlanUseCase: UpdatePlanUseCase;
  deletePlanUseCase: DeletePlanUseCase;
}

const initialState: PlansState = {
  plans: [],
  currentPlan: null,
  loading: false,
  error: null,
};

const PlansContext = createContext<PlansContextValue | null>(null);

export function PlansProvider({
  children,
  getAllPlansUseCase,
  getPlanByIdUseCase,
  createPlanUseCase,
  updatePlanUseCase,
  deletePlanUseCase,
}: PlansProviderProps) {
  const [state, setState] = useState<PlansState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const clearCurrentPlan = useCallback(() => {
    setState((s) => ({ ...s, currentPlan: null }));
  }, []);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const plans = await getAllPlansUseCase.execute();
      setState((s) => ({ ...s, plans, loading: false }));
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      setState((s) => ({
        ...s,
        error: errorMessage,
        plans: [],
        loading: false,
      }));
    }
  }, [getAllPlansUseCase]);

  const getById = useCallback(
    async (id: string): Promise<Plan | null> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const plan = await getPlanByIdUseCase.execute(id);
        setState((s) => ({ ...s, currentPlan: plan, loading: false }));
        return plan;
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({
          ...s,
          error: errorMessage,
          currentPlan: null,
          loading: false,
        }));
        return null;
      }
    },
    [getPlanByIdUseCase]
  );

  const create = useCallback(
    async (plan: Plan): Promise<PlanOperationResult> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await createPlanUseCase.execute(plan);
        const plans = await getAllPlansUseCase.execute();
        setState((s) => ({ ...s, plans, loading: false, error: null }));
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage, loading: false }));
        return { success: false, error: errorMessage };
      }
    },
    [createPlanUseCase, getAllPlansUseCase]
  );

  const update = useCallback(
    async (id: string, data: Partial<Plan>): Promise<PlanOperationResult> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await updatePlanUseCase.execute(id, data);
        const plans = await getAllPlansUseCase.execute();
        setState((s) => ({
          ...s,
          plans,
          currentPlan:
            s.currentPlan?.id === id ? plans.find((p) => p.id === id) ?? null : s.currentPlan,
          loading: false,
          error: null,
        }));
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage, loading: false }));
        return { success: false, error: errorMessage };
      }
    },
    [updatePlanUseCase, getAllPlansUseCase]
  );

  const deletePlan = useCallback(
    async (id: string): Promise<PlanOperationResult> => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await deletePlanUseCase.execute(id);
        const plans = await getAllPlansUseCase.execute();
        setState((s) => ({
          ...s,
          plans,
          currentPlan: s.currentPlan?.id === id ? null : s.currentPlan,
          loading: false,
          error: null,
        }));
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage, loading: false }));
        return { success: false, error: errorMessage };
      }
    },
    [deletePlanUseCase, getAllPlansUseCase]
  );

  const events: PlansEvents = useMemo(
    () => ({
      refresh,
      getById,
      clearCurrentPlan,
      create,
      update,
      delete: deletePlan,
      clearError,
    }),
    [refresh, getById, clearCurrentPlan, create, update, deletePlan, clearError]
  );

  const value: PlansContextValue = useMemo(() => ({ state, events }), [state, events]);

  return <PlansContext.Provider value={value}>{children}</PlansContext.Provider>;
}

export function usePlans(): PlansContextValue {
  const ctx = useContext(PlansContext);
  if (!ctx) {
    throw new Error('usePlans must be used within PlansProvider');
  }
  return ctx;
}
